import React from "react";
import { Container, Row, Col, Image, Card, Button } from "react-bootstrap";
import copperMainImage from "../Assets/Image/fabricatedcopper.jpg";
import copper1 from "../Assets/Image/copper-busbar-1.jpg";
import copper2 from "../Assets/Image/copper-busbar-2.jpg";
import copper3 from "../Assets/Image/copper-busbar-3.jpg";
import copper4 from "../Assets/Image/copper-busbar-4.jpg";
import { useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Helmet } from "react-helmet";

const FabricatedCopperBusbars = () => {
  const navigate = useNavigate();

  const relatedProducts = [
    { name: "Punched Copper busbar", image: copper1 },
    { name: " Multi Bend Copper Busbar", image: copper2 },
    { name: "Custom Fabricated Copper Busbar", image: copper3 },
    { name: "Clinching Nut / Stud fixed Busbars", image: copper4 },
  ];

  return (
    <>
      <Helmet>
        <title>Fabricated Copper Busbars | Delta Engineering</title>
        <meta
          name="description"
          content="Precision-engineered fabricated copper busbars designed for high conductivity, durability, and optimized electrical performance. Available in plain, tin, silver, and nickel-plated finishes."
        />
        <meta
          name="keywords"
          content="Fabricated Copper Busbars, Copper Busbar, Punched Copper Busbar, Multi Bend Copper Busbar, Custom Fabricated Busbar, Copper Busbar Manufacturer, EC Grade Copper Busbar, ETP Copper Busbar"
        />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content="Fabricated Copper Busbars" />
        <meta
          property="og:description"
          content="High-performance copper busbars with exceptional conductivity, mechanical strength, and corrosion resistance. Fully customizable for power panels, switchgear, UPS, and renewable energy applications."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.yourdomain.com/fabricated-copper-busbars"
        />
        <meta
          property="og:image"
          content="https://www.yourdomain.com/images/fabricated-copper-busbars.jpg"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fabricated Copper Busbars" />
        <meta
          name="twitter:description"
          content="Custom fabricated copper busbars crafted with EC/ETP grade copper. Available with tin, silver, or nickel plating for superior performance."
        />
        <meta
          name="twitter:image"
          content="https://www.yourdomain.com/images/fabricated-copper-busbars.jpg"
        />
      </Helmet>
      <section className="productbanner1 d-flex align-items-center position-relative">
        <div className="overlay-layer"></div>
        <Container className="text-center position-relative z-2">
          <Row className="justify-content-center">
            <Col lg={8} className="hero-content text-center">
              <h2
                className="hero-title"
                data-aos="zoom-in"
                data-aos-delay="300"
              >
                <span className="blue-text"> Fabricated Copper</span>{" "}
                <span className="white-text"> Busbars</span>
              </h2>
              <p
                className="hero-subtitle"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                Precision-crafted copper busbars for high-conductivity,
                durability, and optimized electrical performance.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <div
        className="fabricated-copper-section py-5 bg-light pt-3"
        style={{ position: "relative" }}
      >
        <Button onClick={() => navigate("/ourproducts")} className="back-btn">
          <IoMdArrowRoundBack className="back-icon" />
          Back
        </Button>
        <Container>
          <Row className="text-center mb-4">
            <Col>
              <h3 className="fw-bold section-title">
                Fabricated Copper Busbars{" "}
                <span className="title-underline"></span>
              </h3>
              <p className="text-muted mb-4">
                Precision-engineered copper busbars designed for superior
                electrical conductivity and reliability in demanding
                environments.
              </p>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={4}>
              <Image
                src={copperMainImage}
                fluid
                rounded
                alt="Fabricated Copper Busbar"
                height={500}
                width={400}
              />
            </Col>
            <Col md={8}>
              <h5 className="fw-semibold">Key Features:</h5>
              <ul className="text-muted ps-3">
                <li>Exceptional electrical and thermal conductivity</li>
                <li>High mechanical strength and corrosion resistance</li>
                <li>
                  Custom shapes, bends, and drillings for perfect integration
                </li>
                <li>Ideal for high-current, low-voltage applications</li>
                <li>Surface treatments for increased durability</li>
              </ul>

              <h5 className="fw-semibold mt-4">Finish & Coatings:</h5>
              <ul className="text-muted ps-3">
                <li>Plain (bare copper)</li>
                <li>Tin Plated</li>
                <li>Silver Plated</li>
                <li>Nickel Plated (on request)</li>
              </ul>
            </Col>
          </Row>

          <h4 className="fw-bold mt-5 mb-4 text-center">Related Products</h4>
          <Row className="g-4 mb-4">
            {relatedProducts.map((product, index) => (
              <Col key={index} xs={12} sm={6} md={3}>
                <Card className="h-100 text-center shadow-sm border-0">
                  <Card.Img
                    variant="top"
                    src={product.image}
                    alt={product.name}
                    style={{
                      height: "180px",
                      objectFit: "contain",
                      padding: "10px",
                    }}
                  />
                  <Card.Body>
                    <Card.Title className="text-muted fs-6">
                      {product.name}
                    </Card.Title>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <h5 className="fw-semibold">Applications:</h5>
          <ul className="text-muted ps-3">
            <li>Power distribution panels and switchgears</li>
            <li>Control panels and low-voltage assemblies</li>
            <li>UPS and battery systems</li>
            <li>Renewable energy inverters and connections</li>
            <li>Custom industrial equipment</li>
          </ul>

          <h5 className="fw-semibold mt-4">Specifications:</h5>
          <ul className="text-muted ps-3">
            <li>Material: EC Grade Copper / ETP Copper</li>
            <li>Thickness: 3mm to 25mm</li>
            <li>Width: 10mm to 300mm</li>
            <li>Conductivity: Up to 101% IACS</li>
            <li>Available in flat, bent, drilled, or plated forms</li>
          </ul>

          <h5 className="fw-semibold mt-4">Customization:</h5>
          <p className="text-muted">
            We manufacture copper busbars as per your designs or application
            requirements. Services include cutting, punching, bending, plating,
            and surface treatments. All parts are tested for quality and
            conductivity before shipment.
          </p>
        </Container>
      </div>
    </>
  );
};

export default FabricatedCopperBusbars;
